import { Injectable, signal, inject } from '@angular/core';
import { RideDocumentDto } from '../interfaces/ride-firestore';
import { QueryConstraint } from 'firebase/firestore';
import { BehaviorSubject, filter } from 'rxjs';
import { DriverMapRideDto } from '../../features/monitoring-center/interfaces/driver-map-ride';
import { NavigationStart, Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class StateService {
  private readonly router = inject(Router);

  private readonly firebaseAuthToken = new BehaviorSubject<string>('');
  firebaseAuthToken$ = this.firebaseAuthToken.asObservable();

  private readonly firebaseUser = new BehaviorSubject<any>(null);
  firebaseUser$ = this.firebaseUser.asObservable();

  private readonly appliedFilters = new BehaviorSubject<any>({});
  appliedFilters$ = this.appliedFilters.asObservable();

  private readonly displayedRides = new BehaviorSubject<RideDocumentDto[]>([]);
  displayedRides$ = this.displayedRides.asObservable();

  private readonly allFirestoreRides = new BehaviorSubject<RideDocumentDto[]>([]);
  allFirestoreRides$ = this.allFirestoreRides.asObservable();

  private readonly allMapDrivers = new BehaviorSubject<DriverMapRideDto[]>([]);
  allMapDrivers$ = this.allMapDrivers.asObservable();

  private readonly statistics = new BehaviorSubject<{ name: string; value: number; color: string }[]>([]);
  statistics$ = this.statistics.asObservable();

  private readonly equipmentsFiles = new BehaviorSubject<{ equipmenId: number; base64File: string; cdnFileName: string }[]>([]);
  equipmentsFiles$ = this.equipmentsFiles.asObservable();

  /*
    Filtered document ids for getting batches from firebase 
  */
  private readonly filteredIds = new BehaviorSubject<number[]>([]);
  filteredIds$ = this.filteredIds.asObservable();

  /*
    Pending rides toggle state
  */
  private readonly isPendingRides = new BehaviorSubject<boolean>(false);
  isPendingRides$ = this.isPendingRides.asObservable();

  /*
    Pending rides count
  */
  private readonly pendingRidesCount = new BehaviorSubject<number>(0);
  pendingRidesCount$ = this.pendingRidesCount.asObservable();

  private savedQueries: QueryConstraint[][] = [];

  private latestRidesScrollIndex = 0;
  private previousRoute: string = '';

  private cancellationReasonsLookup: { name: string; id: number }[] = [];

  constructor() {
    // Use NavigationStart to capture the route BEFORE navigation completes
    this.router.events.pipe(filter(event => event instanceof NavigationStart)).subscribe((event: NavigationStart) => {
      // Store current route as previous before navigation starts
      console.log(event);
      console.log(this.router.url);

      this.previousRoute = this.router.url;
    });
  }

  setCancellationReasonsLookup(reasons: { name: string; id: number }[]): void {
    this.cancellationReasonsLookup = reasons;
  }

  getCancellationReasonsLookup(): { name: string; id: number }[] {
    return this.cancellationReasonsLookup;
  }

  shouldPreserveState(): boolean {
    console.log('Previous route:', this.previousRoute);
    return !this.previousRoute || this.previousRoute.includes('monitoring-center');
  }

  setRidesLatestScrollIndex(index: number): void {
    this.latestRidesScrollIndex = index;
  }
  getRidesLatestScrollIndex(): number {
    return this.latestRidesScrollIndex;
  }

  setFilteredIds(ids: number[]): void {
    this.filteredIds.next(ids);
  }
  getFilteredIds(): number[] {
    return this.filteredIds.getValue();
  }

  saveQueryConstraints(queries: QueryConstraint[][]): void {
    this.savedQueries = queries;
  }

  getSavedQueryConstraints(): QueryConstraint[][] {
    return this.savedQueries;
  }

  setAllMapDrivers(drivers: DriverMapRideDto[]): void {
    this.allMapDrivers.next(drivers);
  }
  getAllMapDrivers(): DriverMapRideDto[] {
    return this.allMapDrivers.getValue();
  }

  setAuthToken(token: string): void {
    this.firebaseAuthToken.next(token);
  }

  getAuthToken(): string | null {
    return this.firebaseAuthToken.getValue();
  }

  setFirebaseUser(user: any): void {
    this.firebaseUser.next(user);
  }

  getFirebaseUser(): any {
    return this.firebaseUser.getValue();
  }

  setAppliedFilters(user: any): void {
    this.appliedFilters.next(user);
  }

  getAppliedFilters(): any {
    return this.appliedFilters.getValue();
  }

  setDisplayedRides(rides: RideDocumentDto[]): void {
    this.displayedRides.next(rides);
  }

  getDisplayedRides(): RideDocumentDto[] {
    return this.displayedRides.getValue();
  }

  setAllFirestoreRides(rides: RideDocumentDto[]): void {
    this.allFirestoreRides.next(rides);
  }

  getAllFirestoreRides(): RideDocumentDto[] {
    return this.allFirestoreRides.getValue();
  }

  setStatistics(statistics: { name: string; value: number; color: string }[]): void {
    this.statistics.next(statistics);
  }

  getStatistics(): { name: string; value: number; color: string }[] {
    return this.statistics.getValue();
  }

  setEquipmentsFiles(equipmentsFiles: { equipmenId: number; base64File: string; cdnFileName: string }[]): void {
    this.equipmentsFiles.next(equipmentsFiles);
  }

  getEquipmentsFiles(): { equipmenId: number; base64File: string; cdnFileName: string }[] {
    return this.equipmentsFiles.getValue();
  }

  setIsPendingRides(isPending: boolean): void {
    this.isPendingRides.next(isPending);
  }

  getIsPendingRides(): boolean {
    return this.isPendingRides.getValue();
  }

  setPendingRidesCount(count: number): void {
    this.pendingRidesCount.next(count);
  }

  getPendingRidesCount(): number {
    return this.pendingRidesCount.getValue();
  }

  /*
    Ride ID to reorder to top of the list (e.g., after unassigning driver)
  */
  readonly #reorderRideToTopSignal = signal<number | null>(null);
  reorderRideToTop = this.#reorderRideToTopSignal.asReadonly();

  setReorderRideToTop(rideId: number | null): void {
    this.#reorderRideToTopSignal.set(rideId);
  }
}
